import type { SearchItem, TextValueChangeHandler, TrackActionHandler } from '../types';
import { sourceLabel } from '../lib';
import { Play, Plus, Heart, ListPlus, Search, Music } from 'lucide-react';

type SearchSource = 'auto' | 'youtube' | 'soundcloud' | 'spotify';

/** Props for the search result list and action buttons. */
export interface SearchResultsCardProps {
  searchQuery: string;
  isLoading: boolean;
  results: SearchItem[];
  onSearchInputChange: TextValueChangeHandler;
  onSearchSubmit: () => void;
  onQueueTrack: TrackActionHandler;
  onPlayNowTrack: TrackActionHandler;
  onFavoriteTrack: TrackActionHandler;
  onTrackAddToPlaylist: TrackActionHandler;
  searchSource?: SearchSource;
  onSearchSourceChange?: (source: SearchSource) => void;
}

/** Get thumbnail URL - prefer artworkUrl from Lavalink, fallback to YouTube extraction */
function getThumbnailUrl(item: SearchItem): string | null {
  if (item.artworkUrl) return item.artworkUrl;
  if (item.source?.toLowerCase().includes('youtube')) {
    const videoId = item.url?.match(/(?:v=|\/)([a-zA-Z0-9_-]{11})/)?.[1];
    if (videoId) return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }
  return null;
}

const SOURCE_OPTIONS: { id: SearchSource; label: string }[] = [
  { id: 'auto', label: 'All' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'spotify', label: 'Spotify' }
];

export default function SearchResultsCard({
  searchQuery,
  isLoading,
  results,
  onSearchInputChange,
  onSearchSubmit,
  onQueueTrack,
  onPlayNowTrack,
  onFavoriteTrack,
  onTrackAddToPlaylist,
  searchSource = 'auto',
  onSearchSourceChange
}: SearchResultsCardProps) {
  return (
    <div className="y2k-card rounded-[2rem] sm:rounded-[2.5rem] p-4 sm:p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-800 dark:text-white text-sm sm:text-base flex items-center gap-2">
          <Search className="w-4 h-4" />
          Search Results
        </h3>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 bg-white/30 dark:bg-white/10 px-2 py-1 rounded-full">
          {results.length} tracks
        </span>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2 mb-2">
        <input
          className="h-10 rounded-2xl bg-white/40 dark:bg-purple-950/50 border border-white/40 dark:border-purple-700/30 px-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60 focus:border-fuchsia-400/60"
          value={searchQuery}
          onChange={(event) => onSearchInputChange(event.target.value)}
          placeholder="Search tracks..."
          onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
        />
        <button
          className="h-10 rounded-2xl px-4 btn-gradient text-sm font-semibold flex items-center gap-2 outline-none focus:outline-none"
          onClick={onSearchSubmit}
          disabled={isLoading}
        >
          <Search className="w-4 h-4" />
          <span className="hidden sm:inline">Search</span>
        </button>
      </div>
      {onSearchSourceChange && (
        <div className="flex gap-1.5 mb-4">
          {SOURCE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => onSearchSourceChange(opt.id)}
              className={`px-3 py-1 rounded-xl text-xs font-medium transition-all ${
                searchSource === opt.id
                  ? 'btn-gradient'
                  : 'bubble-btn hover:bg-white/60 dark:hover:bg-purple-900/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto pr-1 space-y-3 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-pulse flex space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
            <Music className="w-12 h-12 mb-3 opacity-30" />
            <div className="text-sm">Search for tracks</div>
            <div className="text-xs mt-1 opacity-70">Results will appear here</div>
          </div>
        ) : (
          results.map((item, index) => {
            const thumbnail = getThumbnailUrl(item);
            return (
              <div 
                key={item.id} 
                className="rounded-2xl bubble-btn p-3 hover-lift flex gap-3 items-start group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Thumbnail */}
                <div className="w-16 h-12 rounded-xl bg-gradient-to-br from-pink-400/20 to-cyan-400/20 flex-shrink-0 overflow-hidden border border-white/20">
                  {thumbnail ? (
                    <img 
                      src={thumbnail} 
                      alt="" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 dark:text-white truncate pr-2">{item.title}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 flex-shrink-0"></span>
                    {item.author && <span className="truncate">{item.author}</span>}
                    {item.author && <span className="opacity-50">·</span>}
                    <span className="flex-shrink-0">{sourceLabel(item.source)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-1">
                  <button 
                    className="p-2 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/20 transition-all hover:scale-110" 
                    onClick={() => onQueueTrack(item)}
                    title="Add to Queue"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button 
                    className="p-2 rounded-xl bg-pink-500/10 text-pink-600 dark:text-pink-400 hover:bg-pink-500/20 transition-all hover:scale-110" 
                    onClick={() => onPlayNowTrack(item)}
                    title="Play Now"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                  <button 
                    className="p-2 rounded-xl bg-lime-500/10 text-lime-600 dark:text-lime-400 hover:bg-lime-500/20 transition-all hover:scale-110" 
                    onClick={() => onFavoriteTrack(item)}
                    title="Add to Favorites"
                  >
                    <Heart className="w-4 h-4" />
                  </button>
                  <button 
                    className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 transition-all hover:scale-110" 
                    onClick={() => onTrackAddToPlaylist(item)}
                    title="Add to Playlist"
                  >
                    <ListPlus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
